import { ProgressBar } from '@magicschool/spellbook/components/ProgressBar';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import { Badge, type BadgeProps } from '../Badge';
import { Icon } from '../Icon';
import { IconButton, type IconButtonProps } from '../IconButton';
import { Spinner } from '../Spinner';
import { getFileTypeIcon, getFileTypeLabel, normalizeFileType } from './constants';

type FileItemFileTypeProps = Omit<BadgeProps, 'variant'> & {
  disabled?: boolean;
  isLoading?: boolean;
  /**
   * Display mode for the file type indicator
   * - 'label': Shows file type as an uppercase text label (default)
   * - 'icon': Shows file type as an icon with colored background
   */
  indicator?: 'label' | 'icon';
  /** Badge variant (only used when indicator='label') */
  variant?: BadgeProps['variant'];
  /** Icon size (only used when indicator='icon') */
  iconSize?: 'sm' | 'md' | 'lg';
  /** Status - when 'error', shows error icon instead of file type */
  status?: 'default' | 'error';
  /** Accessible label for the file type icon. Overrides auto-detected category label. */
  fileTypeLabel?: string;
};
type FileItemTitleProps = TextProps & { disabled?: boolean };
type FileItemDescriptionProps = TextProps & { disabled?: boolean; isLoading?: boolean; loading?: string };

export const FileItemFileTypeSlot = Slot<FileItemFileTypeProps>({
  displayName: 'FileItem.FileType',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({
    className,
    disabled,
    isLoading,
    indicator = 'label',
    variant = 'info',
    size,
    iconSize = 'md',
    status = 'default',
    fileTypeLabel,
    children,
    ...props
  }) => {
    if (isLoading) return <Spinner data-spellbook-name="file-item-file-type" color="primary" size="md" />;

    if (status === 'error') {
      return (
        <div data-spellbook-name="file-item-file-type" className={cn('flex-shrink-0', className)} {...props}>
          <Icon name="circle-x" size={iconSize} color="error" role="img" aria-label="File error" />
        </div>
      );
    }

    const normalized = typeof children === 'string' ? normalizeFileType(children) : '';

    if (indicator === 'icon') {
      const iconName = getFileTypeIcon(normalized);
      const label = fileTypeLabel ?? getFileTypeLabel(iconName);

      return (
        <div data-spellbook-name="file-item-file-type" className={cn('flex-shrink-0', className)} {...props}>
          <Icon name={iconName} size={iconSize} color={disabled ? 'disabled' : 'default'} role="img" aria-label={label} />
        </div>
      );
    }

    return (
      <Badge
        data-spellbook-name="file-item-file-type"
        className={cn('uppercase', disabled && 'text-disabled-foreground', className)}
        variant={disabled ? 'default' : variant}
        size={size}
        {...props}
      >
        {normalized || children}
      </Badge>
    );
  },
});

export const FileItemTitleSlot = Slot<FileItemTitleProps>({
  displayName: 'FileItem.Title',
  preventOverride: true,
  renderDefault: ({ children, className, disabled, ...props }) => (
    <Text
      data-spellbook-name="file-item-title"
      color={disabled ? 'disabled' : 'default'}
      variant="caption-1"
      className={cn('truncate', className)}
      {...props}
    >
      {children}
    </Text>
  ),
});

export const FileItemDescriptionSlot = Slot<FileItemDescriptionProps>({
  displayName: 'FileItem.Description',
  preventOverride: true,
  renderDefault: ({ children, className, disabled, isLoading, loading, ...props }) => (
    <Text
      data-spellbook-name="file-item-description"
      color={disabled ? 'disabled' : 'default-soft'}
      variant="caption-2"
      className={cn('whitespace-nowrap', className)}
      {...props}
    >
      {isLoading ? loading : children}
    </Text>
  ),
});

export const FileItemActionsSlot = Slot<React.HTMLAttributes<HTMLDivElement>>({
  displayName: 'FileItem.Actions',
  renderDefault: ({ className, ...props }) => (
    <div data-spellbook-name="file-item-actions" className={cn('flex items-center gap-2', className)} {...props} />
  ),
});

type FileItemActionProps = Omit<IconButtonProps, 'size'> & {
  /** Size of the action button. Defaults to 'xs' for compact display. */
  size?: IconButtonProps['size'];
};

export const FileItemActionSlot = Slot<FileItemActionProps>({
  displayName: 'FileItem.Action',
  renderDefault: ({ size = 'sm', variant = 'ghost', ...props }) => (
    <IconButton data-spellbook-name="file-item-action" size={size} variant={variant} {...props} />
  ),
});

/** Download action with preset icon and label. */
export const FileItemDownloadActionSlot = Slot<Partial<FileItemActionProps>>({
  displayName: 'FileItem.DownloadAction',
  renderDefault: ({ name = 'download', variant = 'tooltip', shape = 'circle', 'aria-label': ariaLabel = 'Download', ...props }) => (
    <FileItemActionSlot
      data-spellbook-name="file-item-download-action"
      name={name}
      variant={variant}
      shape={shape}
      aria-label={ariaLabel}
      {...props}
    />
  ),
});

/** Remove action with preset icon, color, and label. */
export const FileItemRemoveActionSlot = Slot<Partial<FileItemActionProps>>({
  displayName: 'FileItem.RemoveAction',
  renderDefault: ({ name = 'trash-2', variant = 'tooltip', shape = 'circle', 'aria-label': ariaLabel = 'Remove', ...props }) => (
    <FileItemActionSlot
      data-spellbook-name="file-item-remove-action"
      name={name}
      variant={variant}
      shape={shape}
      aria-label={ariaLabel}
      {...props}
    />
  ),
});

type FileItemProgressProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Progress value (0-100) */
  value?: number;
  /** Accessible label for the progress bar. @default "Processing" */
  label?: string;
};

/** Progress bar for file processing progress. Only renders when value is provided. */
export const FileItemProgressSlot = Slot<FileItemProgressProps>({
  displayName: 'FileItem.Progress',
  isRenderedByDefault: true,
  renderDefault: ({ value, label = 'Processing', className, ...props }) => {
    // Don't render if no progress value or complete
    if (value === undefined || value >= 100) return null;

    const clampedValue = Math.min(100, Math.max(0, value));

    return (
      <ProgressBar
        data-spellbook-name="file-item-progress"
        value={clampedValue}
        aria-label={label}
        variant="inline"
        className={className}
        {...props}
      >
        <ProgressBar.Indicator value={clampedValue} hideSpinner />
      </ProgressBar>
    );
  },
});
